import { useState, useEffect } from 'react';
import { fetchAuthSession } from '../utils/session';
import { fetchAdministeredEstateGroups } from './useAdministeredEstateGroups';

export interface UserPermissions {
  /**
   * S-16 — tenant-wide admin: Cognito group `admin` (Cap Cana) or `xhinola_admin`. Opens the
   * tenant menu and routes (all businesses, Users, Estates, Settings).
   */
  isAdmin: boolean;
  /**
   * S-16 — `business_admin`: admin of their own business only (custom:business_id). Gets the
   * business-scoped pages (services, bookings, payments, events), never the tenant ones.
   */
  isBusinessAdmin: boolean;
  isPropertyManager: boolean;
  isInvitationManager: boolean;
  isResidentialInvitationManager: boolean;
  isAllInvitationManager: boolean;
  /**
   * Front Desk of the tenant (group `business_invitation_all`): a tenant role with no `custom:business_id`.
   * It works across every business of the tenant, like the admin, but without configuration, residences,
   * statuses or deletes.
   */
  isTenantFrontDesk: boolean;
  /**
   * SAM-1108 (M7): Estate Group Admin (`estate_group_admin`) and Estate Group Front Desk
   * (`estate_group_front_desk`), Documentation/specs/estate-group-roles.md. No `custom:business_id`: the server
   * scopes them to the estates and businesses of `custom:estate_group_ids`. A tenant group wins over them and a
   * `business*` group makes the token unusable (§1), so neither flag is set then; with both groups, the Front Desk
   * (the more restrictive) is. What they see comes from their matrix in utils/frontDeskMatrix.ts.
   */
  isEstateGroupAdmin: boolean;
  isEstateGroupFrontDesk: boolean;
  /**
   * SAM-1321: the estate group roles of the business model. The user belongs to a business (`custom:business_id`) that
   * administers an estate group (`estate_group.admin_business_id`): its Owner / Admin (`business_owner`,
   * `business_manager`) is the estate group admin, its Operator (`business_operator`) the estate group front desk.
   * The two flags above are set for them too, so every screen reads one pair of flags; this one says which model
   * they came from (the business one keeps Business > Team and only its own business).
   */
  estateGroupViaBusiness: boolean;
  /** SAM-1321: the estate groups the user's business administers (empty unless `estateGroupViaBusiness`). */
  administeredEstateGroups: Array<{ id: number; name: string }>;
  /** Admin, tenant Front Desk or an estate group role: Arrivals and Bookings span every business they reach, with a business selector. */
  seesAllBusinesses: boolean;
  isDashboardOnly: boolean;
  isInvitationOnly: boolean;
  isEventDashboardOnly: boolean;
  isTenantSettings: boolean;
  isPaymentsManager: boolean;
  isUsersManager: boolean;
  isEstatesManager: boolean;
  isEventsManager: boolean;
  isInsightsManager: boolean;
  isCMSManager: boolean;
  isRolesManager: boolean;
  isServicesManager: boolean;
  isBookingsManager: boolean;
  isSetPassword: boolean;
  /**
   * SAM-535 — holds the backend's `xhinola_admin` group, which grants
   * MANAGE_MERCHANT (see src/utils/capabilities.ts).
   *
   * Use this to decide what to SHOW. The server re-checks the group on the
   * token and is what actually allows the write.
   */
  isXhinolaAdmin: boolean;
  /**
   * SAM-714 — may publish / archive events and edit a non-draft event: Cognito
   * group `admin` (Cap Cana admin) or `xhinola_admin`. Everyone else's events
   * are saved as drafts. UI hint only; the CRUD lambda enforces it (SAM-713).
   */
  isEventApprover: boolean;
  isLoading: boolean;
  cognitoId: string | null;
  mainEstate: string | null;
  /** `custom:business_id` on the token: the business a business user belongs to. */
  businessId: string | null;
  email: string | null;
}

export const useUserPermissions = (): UserPermissions => {
  const [permissions, setPermissions] = useState<UserPermissions>({
    isAdmin: false,
    isBusinessAdmin: false,
    isPropertyManager: false,
    isInvitationManager: false,
    isResidentialInvitationManager: false,
    isAllInvitationManager: false,
    isTenantFrontDesk: false,
    isEstateGroupAdmin: false,
    isEstateGroupFrontDesk: false,
    estateGroupViaBusiness: false,
    administeredEstateGroups: [],
    seesAllBusinesses: false,
    isDashboardOnly: false,
    isInvitationOnly: false,
    isEventDashboardOnly: false,
    isTenantSettings: false,
    isPaymentsManager: false,
    isUsersManager: false,
    isEstatesManager: false,
    isEventsManager: false,
    isInsightsManager: false,
    isCMSManager: false,
    isRolesManager: false,
    isServicesManager: false,
    isBookingsManager: false,
    isSetPassword: false,
    isXhinolaAdmin: false,
    isEventApprover: false,
    isLoading: true,
    cognitoId: null,
    mainEstate: null,
    businessId: null,
    email: null,
  });

  useEffect(() => {
    const checkUserGroups = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (idToken) {
          const tokenParts = idToken.split('.');
          if (tokenParts.length === 3) {
            const tokenPayload = JSON.parse(atob(tokenParts[1]));
            const userGroups = tokenPayload['cognito:groups'] || [];
            
            // S-16: `business_admin` is scoped to its own business (capability_matrix.py), so it is
            // not the tenant admin. It gets the business-scoped pages through the flags below.
            const isXhinolaAdmin = userGroups.includes('xhinola_admin');
            const isAdmin = userGroups.includes('admin') || isXhinolaAdmin;
            const isBusinessAdmin = userGroups.includes('business_admin');
            const isPropertyManager = userGroups.includes('business_property_manager');
            const isInvitationManager = userGroups.includes('business_invitation');
            const isResidentialInvitationManager = userGroups.includes('business_residential_invitation');
            const isAllInvitationManager = userGroups.includes('business_invitation_all');
            const hasDashboardGroup = userGroups.includes('dashboard');
            const isTenantSettings = userGroups.includes('tenantSettings');
            const isPaymentsManager = userGroups.includes('business_payments') || isBusinessAdmin;
            const isUsersManager = userGroups.includes('business_users');
            const isEstatesManager = userGroups.includes('business_estates');
            // Events are out of the v1 scope of a business (28 Oct): business_admin no longer implies them. Only the
            // business_events group (and the tenant admin, through isAdmin) manages events.
            const isEventsManager = userGroups.includes('business_events');
            const isInsightsManager = userGroups.includes('business_insights');
            const isCMSManager = userGroups.includes('business_cms');
            const isRolesManager = userGroups.includes('business_roles');
            const isServicesManager = userGroups.includes('business_services') || isBusinessAdmin;
            // SAM-199: front desk (business_invitation, business_residential_invitation,
            // business_invitation_all, business_property_manager) now also gets
            // business-scoped MANAGE_BOOKINGS from the backend capability matrix, so
            // they need isBookingsManager to see the Bookings page/menu entry. Reuse
            // the existing flags for those four groups rather than re-listing the raw
            // group strings, so this stays the single place that knows who is front desk.
            const isFrontDesk = isInvitationManager || isResidentialInvitationManager || isAllInvitationManager || isPropertyManager;
            // "Own" bookings capability, independent of the front-desk grant above.
            // Used by the *Only flags below so that a front-desk grant (which is
            // implied by isInvitationManager/isResidentialInvitationManager/
            // isAllInvitationManager/isPropertyManager and already accounted for by
            // those checks) doesn't get double-counted as an "other" admin capability.
            const hasOwnBookingsGroup = userGroups.includes('business_bookings') || isServicesManager;
            const isBookingsManager = hasOwnBookingsGroup || isFrontDesk;
            const isSetPassword = userGroups.includes('business_set_password');
            const isEventApprover = userGroups.includes('admin') || isXhinolaAdmin;
            const hasEventDashboardGroup = userGroups.includes('business_event_dashboard');
            // SAM-1108: estate group roles, only without a tenant group (admin, xhinola_admin, tenantSettings,
            // dashboard) or a business* group (spec estate-group-roles.md §1).
            const estateGroupEligible = !isAdmin && !isTenantSettings && !hasDashboardGroup
              && !userGroups.some((g: string) => String(g).startsWith('business'));
            const isEstateGroupFrontDesk = estateGroupEligible && userGroups.includes('estate_group_front_desk');
            const isEstateGroupAdmin = estateGroupEligible && !isEstateGroupFrontDesk && userGroups.includes('estate_group_admin');

            // isDashboardOnly es true si solo tiene el grupo 'dashboard' y ningún otro grupo de permisos
            const isDashboardOnly = hasDashboardGroup && !isAdmin && !isPropertyManager && !isInvitationManager && !isResidentialInvitationManager && !isAllInvitationManager && !isPaymentsManager && !isUsersManager && !isEstatesManager && !isEventsManager && !isInsightsManager && !isCMSManager && !isRolesManager && !isServicesManager && !hasOwnBookingsGroup && !isSetPassword;

            // isEventDashboardOnly: solo puede ver el dashboard de los eventos del
            // negocio asignado, nada más. Verdadero cuando tiene el grupo
            // business_event_dashboard y ninguna otra capacidad de administración.
            const isEventDashboardOnly = hasEventDashboardGroup && !isAdmin && !isPropertyManager && !isInvitationManager && !isResidentialInvitationManager && !isAllInvitationManager && !isPaymentsManager && !isUsersManager && !isEstatesManager && !isEventsManager && !isInsightsManager && !isCMSManager && !isRolesManager && !isServicesManager && !hasOwnBookingsGroup;

            // isInvitationOnly: user only has invitation roles (business, residential, or all) and no other admin capability.
            // Front-desk's implied MANAGE_BOOKINGS (isFrontDesk, folded into isBookingsManager above) is not an
            // "other" capability here — it's inherent to being an invitation/property manager — so this checks
            // hasOwnBookingsGroup instead of isBookingsManager to avoid losing the invitations landing experience.
            const isInvitationOnly = !isAdmin && !isPropertyManager && (isInvitationManager || isResidentialInvitationManager || isAllInvitationManager) && !isPaymentsManager && !isUsersManager && !isEstatesManager && !isEventsManager && !isInsightsManager && !isCMSManager && !isRolesManager && !isServicesManager && !hasOwnBookingsGroup && !isTenantSettings;

            // SAM-1321: a business Owner / Admin / Operator may administer an estate group; ask which, once, before
            // saying the permissions are loaded (so the menu does not flash the business one first).
            const businessIdClaim: string | null = tokenPayload['custom:business_id'] || null;
            const isBusinessOwnerOrManager = userGroups.includes('business_owner') || userGroups.includes('business_manager');
            const isBusinessOperator = userGroups.includes('business_operator');
            const mayAdministerGroup = !isAdmin && !isTenantSettings && !hasDashboardGroup && !!businessIdClaim
              && (isBusinessOwnerOrManager || isBusinessOperator);
            const administered = mayAdministerGroup ? await fetchAdministeredEstateGroups(businessIdClaim as string) : [];
            const viaBusiness = administered.length > 0;
            const businessEgFrontDesk = viaBusiness && !isBusinessOwnerOrManager;
            const businessEgAdmin = viaBusiness && isBusinessOwnerOrManager;

            setPermissions({
              isAdmin,
              isBusinessAdmin,
              isPropertyManager,
              isInvitationManager,
              isResidentialInvitationManager,
              isAllInvitationManager,
              // Same as backend #598: business_invitation_all with no tenant group (admin, xhinola_admin, tenantSettings).
              isTenantFrontDesk: isAllInvitationManager && !isAdmin && !isTenantSettings,
              isEstateGroupAdmin: isEstateGroupAdmin || businessEgAdmin,
              isEstateGroupFrontDesk: isEstateGroupFrontDesk || businessEgFrontDesk,
              estateGroupViaBusiness: viaBusiness,
              administeredEstateGroups: administered,
              // The estate group roles span the businesses of their groups; the server narrows every list to them.
              // Through a business they work in their own business (the server confines them to it), so no selector.
              seesAllBusinesses: isAdmin || isAllInvitationManager || isEstateGroupAdmin || isEstateGroupFrontDesk,
              isDashboardOnly,
              isInvitationOnly,
              isEventDashboardOnly,
              isTenantSettings,
              isPaymentsManager,
              isUsersManager,
              isEstatesManager,
              isEventsManager,
              isInsightsManager,
              isCMSManager,
              isRolesManager,
              isServicesManager,
              isBookingsManager,
              isSetPassword,
              isXhinolaAdmin,
              isEventApprover,
              isLoading: false,
              cognitoId: tokenPayload['sub'] || null,
              mainEstate: tokenPayload['custom:mainEstate'] || null,
              businessId: tokenPayload['custom:business_id'] || null,
              email: tokenPayload['email'] || null,
            });
          }
        }
      } catch (error) {
        console.error('Error verificando grupos de usuario:', error);
        setPermissions(prev => ({ ...prev, isLoading: false }));
      }
    };
    
    checkUserGroups();
  }, []);

  return permissions;
};
